import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import { InputComponent } from '../Common/InputComponent.js'
import { WeatherDataParser } from './WeatherDataParser.js'
import { SystemCoolingDesignTemps } from '../BuildingComponents/SystemDesignTemps.js'

import { 
  valOr,
  getMonthName,
  StrictParseNumber,
} from '../SharedUtils.js'
import { IsTestEnv } from '../Globals.js'
export { Units } from '../Common/Units.js'
import * as ser from '../Common/SerUtil.js'
import { WeatherDataMap } from '../Data/WeatherDataMap.js'
import { IsInvalidLocation } from '../Data/InvalidLocations.js'

import {
  FieldType,
  Field,
} from '../Common/Field.js'
import { FieldWithDefault } from '../Common/FieldUtils.js'

// Uses vite's import.meta.glob feature.
// Will generate a list of all weather data files in the WeatherData folder and 
// allow us to dynamically import them as chunks.
const kWeatherDataFiles = import.meta.glob('../Data/WeatherData/**/*.csv', {
  query: '?raw',
  import: 'default',
});

// Flat list of every leaf (selectable) location in WeatherDataMap, as
// {fullName, name, path}. Computed once and cached; WeatherDataMap is a
// static, build-time-generated dataset (see GenWeatherDataMap.py).
let _leafLocationsCache = null;
function getLeafLocations() {
  if (!_leafLocationsCache) {
    let locations = [];
    let walk = (node, path) => {
      if (IsInvalidLocation([...path, node.name])) {
        return;
      }
      if (!node.children) {
        locations.push({ fullName: node.fullName, name: node.name, path: node.path });
        return;
      }
      for (const child of node.children) {
        walk(child, [...path, node.name]);
      }
    }
    for (const child of WeatherDataMap.children) {
      walk(child, []);
    }
    _leafLocationsCache = locations;
  }
  return _leafLocationsCache;
}

// Searches leaf locations by a case-insensitive substring match against
// fullName (e.g. "Chicago" matches "Chicago, IL. United States"). Returns
// {fullName, name, path} objects — pass one directly as the value for
// LocationData.applyAgentValue()/setLocation().
export function searchLocations(query, maxResults = 20) {
  let q = (query ?? '').trim().toLowerCase();
  if (!q) {
    return [];
  }
  return getLeafLocations()
    .filter(loc => loc.fullName.toLowerCase().includes(q))
    .slice(0, maxResults);
}

// Looks up a leaf location by its exact path (e.g. ['Canada', 'MB', 'Winnipeg']).
// Returns null if not found or if the location is on the invalid-locations list.
export function findLocationByPath(path) {
  if (!Array.isArray(path) || path.length === 0 || IsInvalidLocation(path)) {
    return null;
  }
  return getLeafLocations().find(loc =>
    loc.path.length === path.length && loc.path.every((seg, i) => seg === path[i])
  ) ?? null;
}

export class MonthlyValues {
  init(fieldType) {
    let fieldNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]
    for (let fieldName of fieldNames) {
      this[fieldName] = new FieldWithDefault({
        name: fieldName,
        type: fieldType,
      })
    }
    this.serFields = fieldNames;
  }

  setDefaultValues(values) {
    if (!Array.isArray(values) || values.length !== 12) {
      throw new Error(`Invalid values array for MonthlyValues: expected 12 values, got ${values?.length}`);
    }
    
    const fieldNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    for (let i = 0; i < 12; i++) {
      this[fieldNames[i]].data.defaultValue = values[i];
    }
  }

  getFieldAtIndex(index) {
    if (index < 0 || index >= 12) {
      throw new Error(`Invalid index ${index} for MonthlyTemperatures`);
    }
    let fieldName = this.getFieldNameAtIndex(index);
    return this[fieldName];
  }

  getFieldNameAtIndex(index) {
    if (index < 0 || index >= 12) {
      throw new Error(`Invalid index ${index} for MonthlyTemperatures`);
    }
    let fieldNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]
    return fieldNames[index];
  }

  getValue() {
    return [
      this.Jan.getValue(),
      this.Feb.getValue(),
      this.Mar.getValue(),
      this.Apr.getValue(),
      this.May.getValue(),
      this.Jun.getValue(),
      this.Jul.getValue(),
      this.Aug.getValue(),
      this.Sep.getValue(),
      this.Oct.getValue(),
      this.Nov.getValue(),
      this.Dec.getValue(),
    ]
  }
}
setupClass(MonthlyValues)

export class LocationData extends InputComponent {
  init() {
    this.locPath = null;
    this.locData = null;

    this.timezone = new FieldWithDefault({
      name: 'Time zone',
      type: FieldType.Count,
      min: -16,
    })
    this.latitude = new FieldWithDefault({
      name: 'Latitude',
      type: FieldType.Angle,
      min: -90,
      max: 90,
    })
    this.longitude = new FieldWithDefault({
      name: 'Longitude',
      type: FieldType.Angle,
      min: -180,
      max: 180,
    })
    this.elevation = new FieldWithDefault({
      name: 'Elevation',
      type: FieldType.Length,
      min: -1e10,
    })
    this.heating99p6PerDryBulb = new FieldWithDefault({
      name: 'Heating - 99.6% DB',
      type: FieldType.Temperature,
    })
    this.heating99PerDryBulb = new FieldWithDefault({
      name: 'Heating - 99% DB',
      type: FieldType.Temperature,
    })
    this.cooling0p4PerDryBulb = new FieldWithDefault({
      name: 'Cooling - 0.4% DB',
      type: FieldType.Temperature,
    })
    this.cooling0p4PerDryBulbMCWB = new FieldWithDefault({
      name: 'Cooling - 0.4% DB MCWB',
      type: FieldType.Temperature,
    })
    this.cooling1PerDryBulb = new FieldWithDefault({
      name: 'Cooling - 1% DB',
      type: FieldType.Temperature,
    })
    this.cooling1PerDryBulbMCWB = new FieldWithDefault({
      name: 'Cooling - 1% DB MCWB',
      type: FieldType.Temperature,
    })
    this.cooling2PerDryBulb = new FieldWithDefault({
      name: 'Cooling - 2% DB',
      type: FieldType.Temperature,
    })
    this.cooling2PerDryBulbMCWB = new FieldWithDefault({
      name: 'Cooling - 2% DB MCWB',
      type: FieldType.Temperature,
    })
    // Note - currently not used
    /*
    this.cooling0p4PerWetBulb = new FieldWithDefault({
      name: 'Cooling - 0.4% WB',
      type: FieldType.Temperature,
    })
    */
    // Note - currently not used
    /*
    this.cooling0p4PerWetBulbMCDB = new FieldWithDefault({
      name: 'Cooling - 0.4% WB MCDB',
      type: FieldType.Temperature,
    })
    */
    this.avgAnnualTemp = new FieldWithDefault({
      name: 'Average Annual Dry Bulb',
      type: FieldType.Temperature,
    })
    this.meanDailyDryBulbRange = new FieldWithDefault({
      name: 'Mean Daily Dry Bulb Range',
      type: FieldType.TempDiffF,
    })
    this.Tau_b = new FieldWithDefault({
      name: 'Solar Irradiance (Tau b)',
      type: FieldType.Count,
      visible: false,
    })
    this.Tau_d = new FieldWithDefault({
      name: 'Solar Irradiance (Tau d)',
      type: FieldType.Count,
      visible: false,
    })
    this.k_soil = new FieldWithDefault({
      name: 'Soil Conductivity',
      type: FieldType.SoilConductivity,
      // This is the standard value
      value: 0.8,
    })

    // Commercial monthly values:
    this.dryBulbDesignTempsByMonth0p4 = MonthlyValues.create(FieldType.Temperature);
    this.dryBulbDesignTempsByMonth2p0 = MonthlyValues.create(FieldType.Temperature);
    this.dryBulbDesignTempsByMonth5p0 = MonthlyValues.create(FieldType.Temperature);
    this.dryBulbDesignTempsByMonth10p0 = MonthlyValues.create(FieldType.Temperature);

    this.wetBulbDesignTempsByMonth0p4 = MonthlyValues.create(FieldType.Temperature);
    this.wetBulbDesignTempsByMonth2p0 = MonthlyValues.create(FieldType.Temperature);
    this.wetBulbDesignTempsByMonth5p0 = MonthlyValues.create(FieldType.Temperature);
    this.wetBulbDesignTempsByMonth10p0 = MonthlyValues.create(FieldType.Temperature);

    this.dryBulbRangeByMonth = MonthlyValues.create(FieldType.TempDiffF);
    this.wetBulbRangeByMonth = MonthlyValues.create(FieldType.TempDiffF);
    this.tauBValuesByMonth = MonthlyValues.create(FieldType.Count);
    this.tauDValuesByMonth = MonthlyValues.create(FieldType.Count);

    this.generalFields = [
      // General:
      'timezone',
      'latitude',
      'longitude',
      'elevation',
      'avgAnnualTemp',
      'meanDailyDryBulbRange',
      'Tau_b',
      'Tau_d',
      'k_soil',
    ]
    this.allFields = [
      ...this.generalFields,

      // Common design temps:
      'heating99p6PerDryBulb',
      'heating99PerDryBulb',
      
      // House design temps:
      'cooling0p4PerDryBulb',
      'cooling0p4PerDryBulbMCWB',
      'cooling1PerDryBulb',
      'cooling1PerDryBulbMCWB',
      'cooling2PerDryBulb',
      'cooling2PerDryBulbMCWB',
      //'cooling0p4PerWetBulb',
      //'cooling0p4PerWetBulbMCDB',

      // Commercial monthly values:
      'dryBulbDesignTempsByMonth0p4',
      'dryBulbDesignTempsByMonth2p0',
      'dryBulbDesignTempsByMonth5p0',
      'dryBulbDesignTempsByMonth10p0',
      'wetBulbDesignTempsByMonth0p4',
      'wetBulbDesignTempsByMonth2p0',
      'wetBulbDesignTempsByMonth5p0',
      'wetBulbDesignTempsByMonth10p0',
      'dryBulbRangeByMonth',
      'wetBulbRangeByMonth',
      'tauBValuesByMonth',
      'tauDValuesByMonth',
    ]

    this.updater.addWatchEffect('loc-data', () => {
      if (!this.locData) {
        return;
      }
      let hottestMonth = StrictParseNumber(this.locData.Cooling['Hottest Month']);
      let monthName = getMonthName(hottestMonth - 1);

      this.timezone.data.defaultValue = StrictParseNumber(this.locData['Location']['Time Zone'])
      this.latitude.data.defaultValue = StrictParseNumber(this.locData['Location']['Latitude'])
      this.longitude.data.defaultValue = StrictParseNumber(this.locData['Location']['Longitude'])
      this.elevation.data.defaultValue = StrictParseNumber(this.locData['Location']['Elevation'])
      
      this.heating99p6PerDryBulb.data.defaultValue = StrictParseNumber(this.locData['Heating']['99.6 Dry Bulb'])
      this.heating99PerDryBulb.data.defaultValue = StrictParseNumber(this.locData['Heating']['99% Dry Bulb'])

      this.cooling0p4PerDryBulb.data.defaultValue = StrictParseNumber(this.locData['Cooling']['0.4% Dry Bulb'])
      this.cooling0p4PerDryBulbMCWB.data.defaultValue = StrictParseNumber(this.locData['Cooling']['0.4% Dry Bulb MCWB'])
      this.cooling1PerDryBulb.data.defaultValue = StrictParseNumber(this.locData['Cooling']['1% Dry Bulb'])
      this.cooling1PerDryBulbMCWB.data.defaultValue = StrictParseNumber(this.locData['Cooling']['1% Dry Bulb MCWB'])
      this.cooling2PerDryBulb.data.defaultValue = StrictParseNumber(this.locData['Cooling']['2% Dry Bulb'])
      this.cooling2PerDryBulbMCWB.data.defaultValue = StrictParseNumber(this.locData['Cooling']['2% Dry Bulb MCWB'])
      /*
      this.cooling0p4PerWetBulb.data.defaultValue = StrictParseNumber(this.locData['Cooling']['0.4% Wet Bulb'])
      this.cooling0p4PerWetBulbMCDB.data.defaultValue = StrictParseNumber(this.locData['Cooling']['0.4% Wet Bulb MCDB'])
      */

      this.avgAnnualTemp.data.defaultValue = StrictParseNumber(this.locData.Temperatures['Average Dry Bulb']['Annual']);
      this.meanDailyDryBulbRange.data.defaultValue = StrictParseNumber(this.locData.MeanDailyTempRange['Mean Dry Bulb Range'][monthName]);
      this.Tau_b.data.defaultValue = StrictParseNumber(this.locData.SolarIrradiance['Tau,b'][monthName]);
      this.Tau_d.data.defaultValue = StrictParseNumber(this.locData.SolarIrradiance['Tau,d'][monthName]);

      // Set monthly values
      this.dryBulbDesignTempsByMonth0p4.setDefaultValues(this.locData.dryBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp0p4]);
      this.dryBulbDesignTempsByMonth2p0.setDefaultValues(this.locData.dryBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp2p0]);
      this.dryBulbDesignTempsByMonth5p0.setDefaultValues(this.locData.dryBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp5p0]);
      this.dryBulbDesignTempsByMonth10p0.setDefaultValues(this.locData.dryBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp10p0]);
      
      this.wetBulbDesignTempsByMonth0p4.setDefaultValues(this.locData.wetBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp0p4]);
      this.wetBulbDesignTempsByMonth2p0.setDefaultValues(this.locData.wetBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp2p0]);
      this.wetBulbDesignTempsByMonth5p0.setDefaultValues(this.locData.wetBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp5p0]);
      this.wetBulbDesignTempsByMonth10p0.setDefaultValues(this.locData.wetBulbDesignTempsByMonth[SystemCoolingDesignTemps.Temp10p0]);
      
      this.dryBulbRangeByMonth.setDefaultValues(this.locData.dryBulbRangeByMonth);
      this.wetBulbRangeByMonth.setDefaultValues(this.locData.wetBulbRangeByMonth);
      this.tauBValuesByMonth.setDefaultValues(this.locData.tauBValuesByMonth);
      this.tauDValuesByMonth.setDefaultValues(this.locData.tauDValuesByMonth);
    })

    // Note: we serialize the locData so that it is ready on load (and also transfers
    // to workers to run space calculations). However, we re-read it on project read,
    // in case it has changed.
    this.serFields = [
      'locPath',
      'locData',
      ...this.allFields,
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Location Info',
      path: 'location',
    }
  }

  getGeneralFields() {
    return this.generalFields;
  }

  getResidentialDesignTempFields() {
    return [
      'heating99p6PerDryBulb',
      'heating99PerDryBulb',
      'cooling0p4PerDryBulb',
      'cooling0p4PerDryBulbMCWB',
      'cooling1PerDryBulb',
      'cooling1PerDryBulbMCWB',
      'cooling2PerDryBulb',
      'cooling2PerDryBulbMCWB',
      //'cooling0p4PerWetBulb',
      //'cooling0p4PerWetBulbMCDB',
    ];
  }

  getCommercialDesignTempFields() {
    // For commercial, we get cooling from by-month tables instead of
    // from fixed cooling values.
    return [
      'heating99p6PerDryBulb',
      'heating99PerDryBulb',
    ]
  }

  isLocationSet() {
    return this.locData !== null;
  }

  // Interface used by FieldCollector/ChangerTool (see src/Assistant/) to expose
  // and edit components that aren't a plain Field. locPath isn't a Field, so it
  // would otherwise be invisible to read_project — this surfaces it explicitly.
  getAgentSummary() {
    return {
      currentLocation: this.locPath ? { fullName: this.locPath.fullName, path: this.locPath.path } : null,
    };
  }

  // Interface used by ChangerTool for propose_changes' 'field' change type when
  // the target isn't a Field. `value` must be {path: [...]} as returned by
  // search_location — looked up against the real location dataset rather than
  // trusted as-is, so the agent can't set a location that doesn't exist.
  async applyAgentValue(value) {
    if (!value || !Array.isArray(value.path)) {
      throw new Error(
        `Invalid location value: expected {"path": [...]} as returned by search_location ` +
        `(e.g. {"path": ["Canada", "MB", "Winnipeg"]}). Got: ${JSON.stringify(value)}`
      );
    }
    let match = findLocationByPath(value.path);
    if (!match) {
      throw new Error(
        `No location found for path ${JSON.stringify(value.path)}. Use search_location to find a valid path first.`
      );
    }
    await this.setLocation(match);
  }

  async setLocation(locPath) {
    if (!locPath) {
      return;
    }
    console.log("Reloading location data!: ", locPath);
    this.locPath = locPath
    await this.reloadData();
    this.unsetErrorWithId('location-data-load-error');
  }

  async reloadData() {
    if (!this.locPath) {
      this.locData = null;
      return;
    }
    console.log("Reloading weather data CSV. LocPath: ", this.locPath);
    let filePath = '../Data/WeatherData/' + this.locPath.path.join("/") + ".csv"
    console.log("FilePath: " + filePath)
    if (!(filePath in kWeatherDataFiles)) {
      throw new Error(`Could not find weather data file: ${filePath}`);
    }
    // Dynamically load
    let fileData = await kWeatherDataFiles[filePath]();
    if (!fileData || fileData.length == 0) {
      throw new Error(`Null data loaded from file ${filePath}: ${fileData}`);
    }
    //console.log("Loaded weather data: ", fileData);
    this.locData = WeatherDataParser.parseWeatherDataCSV(fileData);
  }

  // Used for testing
  setLocationManually(locPath, locData) {
    this.locPath = locPath;
    this.locData = locData;
  }

  getObjErrors() {
    let errors = [];
    this.updater.addErrors(errors);
    if (!this.isLocationSet()) {
      errors.push('Please set a location.');
    }
    return errors;
  }

  getSampleTemps(opts) {
    opts = valOr(opts, {});
    // Use user given values or fallback to reasonable defaults
    let t_i_winter = valOr(opts.t_i_winter, 70);
    let t_i_summer = valOr(opts.t_i_summer, 75);
    // Return some reasonable default temps for the purposes of preview
    // calculations.
    let temps = {
      t_i_winter: t_i_winter,
      // Note: heating 99p6 DB means the temp is below this value 0.4% of the time
      t_o_winter: this.heating99p6PerDryBulb.getValue(),
      t_i_summer: t_i_summer,
      // Note: cooling 0p4 DB means the temp is above this value 0.4% of the time
      t_o_summer: this.cooling0p4PerDryBulb.getValue(),
    }
    // Some aliases
    temps.t_i_heating = temps.t_i_winter;
    temps.t_o_heating = temps.t_o_winter;
    temps.t_i_cooling = temps.t_i_summer;
    temps.t_o_cooling = temps.t_o_summer;
    return temps;
  }

  getOutputs() {
    if (!this.isLocationSet()) {
      return null;
    }
    let hottestMonth = this.locData.Cooling['Hottest Month'];
    let dryBulbDesignTempsByMonth = {
      [SystemCoolingDesignTemps.Temp0p4]: this.dryBulbDesignTempsByMonth0p4.getValue(),
      [SystemCoolingDesignTemps.Temp2p0]: this.dryBulbDesignTempsByMonth2p0.getValue(),
      [SystemCoolingDesignTemps.Temp5p0]: this.dryBulbDesignTempsByMonth5p0.getValue(),
      [SystemCoolingDesignTemps.Temp10p0]: this.dryBulbDesignTempsByMonth10p0.getValue(),
    }
    let wetBulbDesignTempsByMonth = {
      [SystemCoolingDesignTemps.Temp0p4]: this.wetBulbDesignTempsByMonth0p4.getValue(),
      [SystemCoolingDesignTemps.Temp2p0]: this.wetBulbDesignTempsByMonth2p0.getValue(),
      [SystemCoolingDesignTemps.Temp5p0]: this.wetBulbDesignTempsByMonth5p0.getValue(),
      [SystemCoolingDesignTemps.Temp10p0]: this.wetBulbDesignTempsByMonth10p0.getValue(),
    }
    let outputs = {
      locationName: this.locPath.fullName,
      hottestMonth,
      dryBulbDesignTempsByMonth: dryBulbDesignTempsByMonth,
      wetBulbDesignTempsByMonth: wetBulbDesignTempsByMonth,
      dryBulbRangeByMonth: this.dryBulbRangeByMonth,
      wetBulbRangeByMonth: this.wetBulbRangeByMonth,
      tauBValuesByMonth: this.tauBValuesByMonth,
      tauDValuesByMonth: this.tauDValuesByMonth,
    }
    for (const fieldName of this.allFields) {
      let field = this[fieldName];
      outputs[fieldName] = field.getValue();
    }
    return outputs;
  }
};
setupClass(LocationData)
